'use client';

/**
 * Audio level engine — ONE rAF loop producing smoothed 0..1 levels for the
 * user mic and agent playback analysers, plus the derived VizMode, for the
 * Sara voice orb.
 *
 * This replaces four independently-smoothed stages that used to run at once
 * (a per-source rAF RMS loop inside useCallMode, a second inside
 * useAudioPlayback, an EMA decay here driven by a 50ms setInterval, and two
 * further envelopes inside the since-deleted Three.js visualizer) with a
 * single attack/release envelope follower serviced by a single loop here.
 * Callers register/unregister their AnalyserNode via
 * `registerUserAnalyser`/`registerAgentAnalyser`; this hook owns reading them.
 * The loop runs only while at least one analyser is registered — text-only
 * chat keeps the page fully idle (no rAF scheduled).
 *
 * Output lives on `levelsRef` — a stable object mutated in place every
 * frame. Renderers (voice/call-orb.tsx, equalizer-bars.tsx) read
 * `levelsRef.current` directly inside their own per-frame callbacks; nothing
 * here calls setState for level, so holding the ref never causes a
 * component to re-render on the audio clock. VizMode is the one piece that
 * stays in React state — it changes rarely (edge-triggered off activity
 * timestamps, guarded so same-value transitions are no-ops) so a normal
 * re-render is the right tool for it.
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { VizMode } from '@/types/chat';

// ─── Configuration ──────────────────────────────────────────────

/** Attack time constant (s) — how fast the envelope rises toward a louder
 *  target. Short so a word's onset registers on the orb immediately. */
const ATTACK_TAU_S = 0.025;
/** Release time constant (s) — how fast the envelope falls back down.
 *  Longer than attack so natural gaps between words/breaths don't read as
 *  the orb visually chopping to silence. */
const RELEASE_TAU_S = 0.2;

/**
 * Per-source calibration gain, applied once to raw RMS before the envelope.
 *
 * The previous implementation applied the agent's ~3x gain TWICE — once
 * baked into an EMA blend, once again at readout (`level * 3`) — compounding
 * to ≈9x, while the user path applied its 5x gain only once. This collapses
 * each source back to the single value it was actually calibrated at: mic
 * input reads quieter than TTS playback at the same perceived loudness,
 * which is why the user gain is higher.
 */
const USER_GAIN = 5;
const AGENT_GAIN = 3;

/** Raw-RMS floor (pre-gain) above which a source counts as "active" for
 *  VizMode purposes. Distinct from the envelope's own release decay — this
 *  gates the activity TIMESTAMP, not the displayed level. */
const USER_ACTIVITY_RMS = 0.01;
const AGENT_ACTIVITY_RMS = 0.005;

/** How long a source's last-activity timestamp keeps it "active" after its
 *  RMS drops below threshold — absorbs brief dips (a consonant, a breath) so
 *  VizMode doesn't flicker between USER/AGENT/IDLE within one utterance. */
const ACTIVITY_TIMEOUT_MS = 300;

/** Stride for RMS sampling — every 4th sample of the analyser's time-domain
 *  buffer, cheap and plenty accurate for a visual meter. */
const RMS_STRIDE = 4;

// ─── Types ──────────────────────────────────────────────────────

/** Engine output. A single stable object — renderers hold a ref to it and
 *  read `.current` per frame; the object identity never changes, only its
 *  fields do, every animation frame. */
export interface AudioLevels {
  /** Smoothed 0..1 mic input level. */
  user: number;
  /** Smoothed 0..1 agent (TTS) playback level. */
  agent: number;
  /** Whichever of user/agent VizMode currently favors — 0 outside
   *  USER/AGENT (IDLE; THINKING drives its own LFO inside the orb, see
   *  voice/call-orb.tsx). This is what orb/bars renderers should read for
   *  "the" current amplitude. */
  active: number;
}

// ─── Pure helpers (unit-tested in useAudioVisualization.test.ts) ────────

/**
 * One step of an asymmetric attack/release envelope follower.
 *
 * `k = 1 - exp(-dt/tau)` is the standard exponential-smoothing step for time
 * constant `tau` over elapsed time `dt` (seconds): using the shorter attack
 * tau while rising and the longer release tau while falling is what gives
 * the envelope its "snaps up, eases down" character instead of a symmetric
 * (and audibly/visually mushier) single-tau smoother.
 */
export function stepEnvelope(
  previous: number,
  target: number,
  dtSeconds: number,
  attackTauS: number = ATTACK_TAU_S,
  releaseTauS: number = RELEASE_TAU_S
): number {
  const tau = target > previous ? attackTauS : releaseTauS;
  const k = 1 - Math.exp(-dtSeconds / tau);
  return previous + (target - previous) * k;
}

/** Stride-sampled time-domain RMS. */
export function calculateRMSLevel(audioData: Float32Array): number {
  let sumSquares = 0;
  let count = 0;
  for (let i = 0; i < audioData.length; i += RMS_STRIDE) {
    sumSquares += audioData[i] * audioData[i];
    count++;
  }
  return count > 0 ? Math.sqrt(sumSquares / count) : 0;
}

// ─── Hook ───────────────────────────────────────────────────────

export function useAudioVisualization(
  isThinkingRef: React.MutableRefObject<boolean>
) {
  const [vizMode, setVizModeState] = useState<VizMode>(VizMode.IDLE);
  // Mirrors vizMode without waiting on React's commit — read inside the rAF
  // loop so a same-value transition is a no-op (guards the "edge-triggered"
  // requirement without depending on React to bail out setState for us).
  const lastVizModeRef = useRef<VizMode>(VizMode.IDLE);

  const setVizMode = useCallback((mode: VizMode) => {
    if (lastVizModeRef.current === mode) return;
    lastVizModeRef.current = mode;
    setVizModeState(mode);
  }, []);

  // AnalyserNode refs — populated via registerUserAnalyser/registerAgentAnalyser
  // by useCallMode (mic) and useAudioPlayback (agent playback) respectively.
  // Those hooks own the node's lifecycle (create/connect/disconnect); this
  // engine only tracks which node to read.
  const userAnalyserNodeRef = useRef<AnalyserNode | null>(null);
  const agentAnalyserNodeRef = useRef<AnalyserNode | null>(null);
  const userAudioTimeDataRef = useRef<Float32Array | null>(null);
  const agentAudioTimeDataRef = useRef<Float32Array | null>(null);

  // Engine output — see AudioLevels above. Mutated in place every tick.
  const levelsRef = useRef<AudioLevels>({ user: 0, agent: 0, active: 0 });

  const lastUserActivityRef = useRef(0);
  const lastAgentActivityRef = useRef(0);

  /** Force an immediate return to IDLE, bypassing the natural
   *  ACTIVITY_TIMEOUT_MS decay — for explicit stop/interrupt transitions
   *  (call end, playback interrupt, tool-call end, reconnect) where the UI
   *  should snap the orb back to rest rather than visually fade out over the
   *  next ~300ms. */
  const resetToIdle = useCallback(() => {
    lastUserActivityRef.current = 0;
    lastAgentActivityRef.current = 0;
    levelsRef.current.user = 0;
    levelsRef.current.agent = 0;
    levelsRef.current.active = 0;
    setVizMode(VizMode.IDLE);
  }, [setVizMode]);

  // The one rAF loop — but gated: it runs ONLY while at least one analyser is
  // registered (a call's mic and/or agent playback). A parked loop keeps the
  // page idle in text-only chat (an unconditional loop held the compositor at
  // ~120fps for the whole session — measured in the first-message-flicker
  // trace). Parking is safe because nothing reads per-frame output when no
  // source exists: levelsRef consumers (call-screen orb/bars) only mount
  // during calls, and VizMode transitions are edge-set directly by the event
  // handlers (setVizMode(THINKING) at TOOL_START, resetToIdle() at
  // TOOL_END/interrupts) — the loop only re-asserts them.
  const rafIdRef = useRef(0);
  const loopRunningRef = useRef(false);
  const lastTsRef = useRef(0);

  const stopLoop = useCallback(() => {
    if (!loopRunningRef.current) return;
    loopRunningRef.current = false;
    cancelAnimationFrame(rafIdRef.current);
    // Settle to rest values so a parked engine never freezes a mid-decay
    // level (consumers that mount later read 0, same as after resetToIdle).
    levelsRef.current.user = 0;
    levelsRef.current.agent = 0;
    levelsRef.current.active = 0;
    // The deleted tick was the only IDLE-decay: parking mid-conversation
    // (last analyser unregisters while vizMode is USER/AGENT) would freeze
    // the orb in a speaking state forever. Mirror the tick's own rule —
    // THINKING is owned by the event handlers (TOOL_END's resetToIdle), so
    // only non-thinking states decay here.
    if (!isThinkingRef.current) setVizMode(VizMode.IDLE);
  }, [isThinkingRef, setVizMode]);

  const startLoop = useCallback(() => {
    if (loopRunningRef.current) return;
    loopRunningRef.current = true;
    lastTsRef.current = performance.now();

    const tick = (ts: number) => {
      if (!loopRunningRef.current) return;
      // Clamp dt so a tab-backgrounding stall doesn't snap the envelope in
      // one giant step when the tab regains focus.
      const dt = Math.min(0.05, (ts - lastTsRef.current) / 1000);
      lastTsRef.current = ts;

      // --- User (mic) ---
      const userAnalyser = userAnalyserNodeRef.current;
      const userTimeData = userAudioTimeDataRef.current;
      let userTarget = 0;
      if (userAnalyser && userTimeData) {
        userAnalyser.getFloatTimeDomainData(userTimeData);
        const rms = calculateRMSLevel(userTimeData);
        userTarget = Math.min(rms * USER_GAIN, 1);
        if (rms > USER_ACTIVITY_RMS) lastUserActivityRef.current = ts;
      }
      levelsRef.current.user = stepEnvelope(
        levelsRef.current.user,
        userTarget,
        dt
      );

      // --- Agent (playback) ---
      const agentAnalyser = agentAnalyserNodeRef.current;
      const agentTimeData = agentAudioTimeDataRef.current;
      let agentTarget = 0;
      if (agentAnalyser && agentTimeData) {
        agentAnalyser.getFloatTimeDomainData(agentTimeData);
        const rms = calculateRMSLevel(agentTimeData);
        agentTarget = Math.min(rms * AGENT_GAIN, 1);
        if (rms > AGENT_ACTIVITY_RMS) lastAgentActivityRef.current = ts;
      }
      levelsRef.current.agent = stepEnvelope(
        levelsRef.current.agent,
        agentTarget,
        dt
      );

      // --- VizMode (edge-triggered) + the "active" level renderers read ---
      if (isThinkingRef.current) {
        setVizMode(VizMode.THINKING);
        levelsRef.current.active = 0;
      } else {
        const userActive =
          ts - lastUserActivityRef.current < ACTIVITY_TIMEOUT_MS;
        const agentActive =
          ts - lastAgentActivityRef.current < ACTIVITY_TIMEOUT_MS;

        if (userActive) {
          setVizMode(VizMode.USER);
          levelsRef.current.active = levelsRef.current.user;
        } else if (agentActive) {
          setVizMode(VizMode.AGENT);
          levelsRef.current.active = levelsRef.current.agent;
        } else {
          setVizMode(VizMode.IDLE);
          levelsRef.current.active = 0;
        }
      }

      rafIdRef.current = requestAnimationFrame(tick);
    };

    rafIdRef.current = requestAnimationFrame(tick);
  }, [isThinkingRef, setVizMode]);

  /** Run the loop iff a source is registered; called from both register
   *  functions so registration IS the loop's lifecycle. */
  const syncLoopToSources = useCallback(() => {
    if (userAnalyserNodeRef.current || agentAnalyserNodeRef.current) {
      startLoop();
    } else {
      stopLoop();
    }
  }, [startLoop, stopLoop]);

  // Cancel the loop when ChatProvider unmounts.
  useEffect(() => stopLoop, [stopLoop]);

  const registerUserAnalyser = useCallback(
    (analyser: AnalyserNode | null) => {
      userAnalyserNodeRef.current = analyser;
      userAudioTimeDataRef.current = analyser
        ? new Float32Array(analyser.fftSize)
        : null;
      syncLoopToSources();
    },
    [syncLoopToSources]
  );

  const registerAgentAnalyser = useCallback(
    (analyser: AnalyserNode | null) => {
      agentAnalyserNodeRef.current = analyser;
      agentAudioTimeDataRef.current = analyser
        ? new Float32Array(analyser.fftSize)
        : null;
      syncLoopToSources();
    },
    [syncLoopToSources]
  );

  return {
    vizMode,
    setVizMode,
    resetToIdle,
    /** Read-only from the outside: renderers hold this ref and pull
     *  `.current.{user,agent,active}` inside their own per-frame callback. */
    levelsRef,
    registerUserAnalyser,
    registerAgentAnalyser,
  };
}
